import type { ReactElement } from 'react';

import { ensureDefined, invariant } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import { RESOLVER_CONTENT_CLASSNAME } from '@e-socna/printer-contract';
import type {
  PaymentMethodId,
  PaymentTermId,
  ValidityPeriodId,
} from '@e-socna/data-layer';

import { useMissionContextOptional } from '../../../contexts/MissionContext/hooks/useMissionContextOptional.hook';
import { MissionQuotationPage_QuotationInfo_Fragment } from '../../../pages/MissionQuotation/fragments/quotation-info.fragment';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { MissionContextStatus } from '../../../contexts/MissionContext/types/mission-context.type';

import type {
  ResolverParameters,
  ResolverResult,
} from '../types/resolvers.type';

export const useDevisReglement = ({
  enabled,
}: Readonly<ResolverParameters>): ResolverResult<ReactElement> => {
  const missionContext = useMissionContextOptional();

  const { paymentMethodsIdMap, validityPeriodsIdMap, paymentTermsIdMap } =
    useReferencesContext();

  if (!enabled) {
    return {
      data: undefined,
      error: null,
      isSuccess: false,
      isLoading: false,
    };
  }

  invariant(!!missionContext, 'Mission context is required');

  const { status, mission } = missionContext;

  switch (status) {
    case MissionContextStatus.Error:
      return {
        data: undefined,
        error: new Error('Error loading mission context'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Loading:
      return {
        data: undefined,
        error: null,
        isSuccess: false,
        isLoading: true,
      };
    case MissionContextStatus.NotInitialized:
      return {
        data: undefined,
        error: new Error('Mission context not initialized'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Ready: {
      const {
        quotationInfo: {
          header: { paymentMethodId, validityPeriodId, paymentTermId },
        },
      } = ensureDefined({
        quotationInfo: getFragmentData(
          MissionQuotationPage_QuotationInfo_Fragment,
          mission.quotationInfo,
        ),
      });

      const { paymentMethod, paymentTerm, validityPeriod } = ensureDefined({
        paymentMethod: paymentMethodsIdMap[paymentMethodId as PaymentMethodId],
        validityPeriod:
          validityPeriodsIdMap[validityPeriodId as ValidityPeriodId],
        paymentTerm: paymentTermsIdMap[paymentTermId as PaymentTermId],
      });

      const content = (
        <div className={RESOLVER_CONTENT_CLASSNAME}>
          <p>
            Les honoraires et frais seront versés à la remise du rapport.
            <br />
            Conditions de Réglement : {paymentTerm.label}
            <br />
            Moyen de Paiement : {paymentMethod.label}
            <br />
            Durée de Validité du Devis : {validityPeriod.label}
            <br />
            L&apos;acceptation de cette offre prend effet après envoi de la
            présente, dûment datée et signée, revêtue de la mention &quot;BON
            POUR ACCORD&quot;.
          </p>
        </div>
      );

      return {
        data: { generated: content },
        error: null,
        isSuccess: true,
        isLoading: false,
      };
    }
    default: {
      throw new Error('Unhandled mission context status');
    }
  }
};
